// Single tabbed dialog covering every per-club setting. Replaces the older
// pair of ClubEditDialog + ClubFeaturesDialog. Each tab tracks its own dirty
// state; saving fires only the PUTs whose tab actually changed, so saving from
// the "Booking limits" tab doesn't bounce against the contact-channel rules.

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import {
  clubIdentityUpdateSchema,
  clubFeaturesSchema,
  clubLimitsSchema,
  type SubscriptionTier,
} from "@sportscanner/schemas";
import { apiPut, getApiErrorCode } from "@/lib/api";

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export interface ClubSettingsSport {
  id: string;
  name: string;
}

export interface ClubSettingsRow {
  id: string;
  slug: string;
  name: string;
  // ISO timestamp the club is paid through, or null (never subscribed). SUPERADMIN
  // edits via the Subscription tab; everywhere else this is read-only.
  subscribedUntil: string | null;
  // Tier — BASIC is the entry-level paid plan, PRO unlocks every membership /
  // OTP / limit / spectator feature, ENTERPRISE is reserved for multi-location
  // / white-label. New clubs land on PRO during the 14-day trial set by
  // onboarding (the trial is just PRO + a short subscribedUntil).
  subscriptionTier: SubscriptionTier;
  // SUPERADMIN-set cap on number of courts. Null = unlimited.
  maxCourts: number | null;
  // Contact + account flags
  collectPhone: boolean;
  requirePhone: boolean;
  requirePhoneOtp: boolean;
  sendBookingConfirmationSms: boolean;
  collectEmail: boolean;
  requireEmail: boolean;
  requireEmailOtp: boolean;
  sendBookingConfirmationEmail: boolean;
  requireLogin: boolean;
  requireMembership: boolean;
  allowMemberOnlySlots: boolean;
  // Limits
  limitBookingsPerContactPerDay: boolean;
  maxBookingsPerContactPerDay: number;
  // Per-user cap on bookings created per calendar day. null = no cap.
  maxBookingsPerCreationDay: number | null;
  enforceBookingQuotas: boolean;
  maxBookingsPerMemberPerDay: number | null;
  preventConsecutiveDayBooking: boolean;
  lateBookingExemptionHours: number | null;
  allowPrimeTimeSlots: boolean;
  maxPrimeBookingsPerWeek: number | null;
  requirePlayerRoster: boolean;
  showPublicSchedule: boolean;
  sports: ClubSettingsSport[];
}

interface Props {
  club: ClubSettingsRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void> | void;
}

type TabKey = "general" | "account" | "contact" | "limits";

export default function ClubSettingsDialog({
  club,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>("general");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // ── General ─────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  // ── Account & membership ───────────────────────────────────────────────
  const [requireLogin, setRequireLogin] = useState(false);
  const [requireMembership, setRequireMembership] = useState(false);
  const [allowMemberOnlySlots, setAllowMemberOnlySlots] = useState(false);
  const [allowPrimeTimeSlots, setAllowPrimeTimeSlots] = useState(false);
  const [showPublicSchedule, setShowPublicSchedule] = useState(false);

  // ── Contact channels ────────────────────────────────────────────────────
  const [collectPhone, setCollectPhone] = useState(false);
  const [requirePhone, setRequirePhone] = useState(true);
  const [requirePhoneOtp, setRequirePhoneOtp] = useState(false);
  const [sendBookingConfirmationSms, setSendBookingConfirmationSms] =
    useState(false);
  const [collectEmail, setCollectEmail] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [requireEmailOtp, setRequireEmailOtp] = useState(false);
  const [sendBookingConfirmationEmail, setSendBookingConfirmationEmail] =
    useState(false);

  // ── Booking limits ──────────────────────────────────────────────────────
  const [limitBookingsPerContactPerDay, setLimitBookingsPerContactPerDay] =
    useState(true);
  // Stored as strings while editing so the input can be cleared without
  // snapping back to 0. Coerced on submit.
  const [maxBookingsPerContactPerDay, setMaxBookingsPerContactPerDay] =
    useState("2");
  // Empty string = no cap (sent as null). Stored as string while editing.
  const [maxBookingsPerCreationDay, setMaxBookingsPerCreationDay] =
    useState("");

  const [enforceBookingQuotas, setEnforceBookingQuotas] = useState(false);
  const [maxBookingsPerMemberPerDay, setMaxBookingsPerMemberPerDay] =
    useState("1");
  const [preventConsecutiveDayBooking, setPreventConsecutiveDayBooking] =
    useState(false);
  const [lateBookingExemptionHours, setLateBookingExemptionHours] =
    useState("");
  // Empty string = no cap (sent as null). Stored as string while editing.
  const [maxPrimeBookingsPerWeek, setMaxPrimeBookingsPerWeek] = useState("");
  const [requirePlayerRoster, setRequirePlayerRoster] = useState(false);

  useEffect(() => {
    if (!open || !club) return;
    setActiveTab("general");
    setErrorCode(null);
    setName(club.name);
    setSlug(club.slug);

    setRequireLogin(club.requireLogin);
    setRequireMembership(club.requireMembership);
    setAllowMemberOnlySlots(club.allowMemberOnlySlots);
    setAllowPrimeTimeSlots(club.allowPrimeTimeSlots);
    setShowPublicSchedule(club.showPublicSchedule);

    setCollectPhone(club.collectPhone);
    setRequirePhone(club.requirePhone);
    setRequirePhoneOtp(club.requirePhoneOtp);
    setSendBookingConfirmationSms(club.sendBookingConfirmationSms);
    setCollectEmail(club.collectEmail);
    setRequireEmail(club.requireEmail);
    setRequireEmailOtp(club.requireEmailOtp);
    setSendBookingConfirmationEmail(club.sendBookingConfirmationEmail);

    setLimitBookingsPerContactPerDay(club.limitBookingsPerContactPerDay);
    setMaxBookingsPerContactPerDay(String(club.maxBookingsPerContactPerDay));
    setMaxBookingsPerCreationDay(
      club.maxBookingsPerCreationDay !== null
        ? String(club.maxBookingsPerCreationDay)
        : "",
    );
    setEnforceBookingQuotas(club.enforceBookingQuotas);
    setMaxBookingsPerMemberPerDay(
      club.maxBookingsPerMemberPerDay !== null
        ? String(club.maxBookingsPerMemberPerDay)
        : "1",
    );
    setPreventConsecutiveDayBooking(club.preventConsecutiveDayBooking);
    setLateBookingExemptionHours(
      club.lateBookingExemptionHours !== null
        ? String(club.lateBookingExemptionHours)
        : "",
    );
    setMaxPrimeBookingsPerWeek(
      club.maxPrimeBookingsPerWeek !== null
        ? String(club.maxPrimeBookingsPerWeek)
        : "",
    );
    setRequirePlayerRoster(club.requirePlayerRoster);
  }, [open, club]);

  // ── Effective values + cross-rule normalisation ────────────────────────
  // Mirrors the server-side normalisation in clubs-admin.ts /features so the
  // UI's read of "what's actually enforced" never lies. `requirePhone`
  // governs both surfaces (public form + admin booking dialog) — OTP only
  // makes sense when the channel is on, and confirmation messages need a
  // contact source (the channel OR a logged-in player account).
  const eff = useMemo(() => {
    const eRequireLogin = requireLogin || requireMembership;
    // `require*` implies `collect*`; when login is required the account is the
    // contact source, so the per-booking phone/email channels are hidden (see
    // the Contact tab) and pinned off so the saved state matches the UI.
    const eCollectPhone = eRequireLogin ? false : collectPhone || requirePhone;
    const eRequirePhone = eRequireLogin ? false : requirePhone;
    const eCollectEmail = eRequireLogin ? false : collectEmail || requireEmail;
    const eRequireEmail = eRequireLogin ? false : requireEmail;
    // OTP toggles + SMS confirmations are hidden from the UI (ENTERPRISE-only
    // and need a per-tenant SMS provider) — pin all three off on save so any
    // stale-true flags get cleared on the next write.
    const ePhoneOtp = false;
    const eSendSms = false;
    const eEmailOtp = false;
    const eSendEmail =
      (eCollectEmail || eRequireLogin) && sendBookingConfirmationEmail;
    return {
      collectPhone: eCollectPhone,
      requirePhone: eRequirePhone,
      requirePhoneOtp: ePhoneOtp,
      sendBookingConfirmationSms: eSendSms,
      collectEmail: eCollectEmail,
      requireEmail: eRequireEmail,
      requireEmailOtp: eEmailOtp,
      sendBookingConfirmationEmail: eSendEmail,
      requireLogin: eRequireLogin,
      requireMembership,
      allowMemberOnlySlots,
      allowPrimeTimeSlots,
      showPublicSchedule,
    };
  }, [
    collectPhone,
    requirePhone,
    collectEmail,
    requireEmail,
    sendBookingConfirmationEmail,
    requireLogin,
    requireMembership,
    allowMemberOnlySlots,
    allowPrimeTimeSlots,
    showPublicSchedule,
  ]);

  // ── Validation ──────────────────────────────────────────────────────────
  const slugInvalid = slug.trim().length > 0 && !SLUG_RE.test(slug.trim());

  const maxContactNum = Number.parseInt(maxBookingsPerContactPerDay, 10);
  const maxContactInvalid =
    limitBookingsPerContactPerDay &&
    (!Number.isFinite(maxContactNum) ||
      maxContactNum < 1 ||
      maxContactNum > 100);

  const creationDayNum = maxBookingsPerCreationDay.trim()
    ? Number.parseInt(maxBookingsPerCreationDay, 10)
    : null;
  const creationDayInvalid =
    creationDayNum !== null &&
    (!Number.isFinite(creationDayNum) ||
      creationDayNum < 1 ||
      creationDayNum > 100);

  const memberDailyNum = Number.parseInt(maxBookingsPerMemberPerDay, 10);
  const lateExemptionNum = lateBookingExemptionHours.trim()
    ? Number.parseInt(lateBookingExemptionHours, 10)
    : null;
  const primeWeeklyNum = maxPrimeBookingsPerWeek.trim()
    ? Number.parseInt(maxPrimeBookingsPerWeek, 10)
    : null;
  const memberQuotaSectionInvalid =
    enforceBookingQuotas &&
    (!Number.isFinite(memberDailyNum) ||
      memberDailyNum < 1 ||
      memberDailyNum > 100 ||
      (lateExemptionNum !== null &&
        (lateExemptionNum < 1 || lateExemptionNum > 72)) ||
      (allowPrimeTimeSlots &&
        primeWeeklyNum !== null &&
        (!Number.isFinite(primeWeeklyNum) ||
          primeWeeklyNum < 1 ||
          primeWeeklyNum > 100)));

  const name2 = name.trim();
  const slug2 = slug.trim();
  const isValid =
    name2.length >= 2 &&
    slug2.length >= 2 &&
    !slugInvalid &&
    !maxContactInvalid &&
    !creationDayInvalid &&
    !memberQuotaSectionInvalid;

  // Each booking-limit block keys on an identifier the booking must carry, so
  // only show the blocks whose identifier the club actually collects — no cap
  // is shown that can't bind (e.g. a per-contact cap with no phone or email).
  // Account identity comes from login/membership; contact identity from the
  // phone or email channel. Per-member quotas count by account, so they also
  // apply when member-only slots exist (those bookings always carry an account).
  const hasContactChannel = eff.collectPhone || eff.collectEmail;
  const canIdentifyBooker = hasContactChannel || eff.requireLogin;
  const showCreationDayLimit = canIdentifyBooker;
  const showContactLimit = hasContactChannel && !eff.requireLogin;
  const showMemberLimit = eff.requireLogin || allowMemberOnlySlots;
  const showLimitsIdentityHint =
    !showCreationDayLimit && !showContactLimit && !showMemberLimit;

  // ── Dirty tracking, per save target ─────────────────────────────────────
  const dirty = useMemo(() => {
    if (!club)
      return {
        identity: false,
        features: false,
        limits: false,
      };
    const identity = name2 !== club.name || slug2 !== club.slug;
    const features =
      eff.collectPhone !== club.collectPhone ||
      eff.requirePhone !== club.requirePhone ||
      eff.requirePhoneOtp !== club.requirePhoneOtp ||
      eff.sendBookingConfirmationSms !== club.sendBookingConfirmationSms ||
      eff.collectEmail !== club.collectEmail ||
      eff.requireEmail !== club.requireEmail ||
      eff.requireEmailOtp !== club.requireEmailOtp ||
      eff.sendBookingConfirmationEmail !== club.sendBookingConfirmationEmail ||
      eff.requireLogin !== club.requireLogin ||
      eff.requireMembership !== club.requireMembership ||
      eff.allowMemberOnlySlots !== club.allowMemberOnlySlots ||
      eff.allowPrimeTimeSlots !== club.allowPrimeTimeSlots ||
      eff.showPublicSchedule !== club.showPublicSchedule;
    const limits =
      limitBookingsPerContactPerDay !== club.limitBookingsPerContactPerDay ||
      (Number.isFinite(maxContactNum) &&
        maxContactNum !== club.maxBookingsPerContactPerDay) ||
      (creationDayNum ?? null) !== (club.maxBookingsPerCreationDay ?? null) ||
      enforceBookingQuotas !== club.enforceBookingQuotas ||
      (Number.isFinite(memberDailyNum)
        ? memberDailyNum !== (club.maxBookingsPerMemberPerDay ?? 0)
        : club.maxBookingsPerMemberPerDay !== null) ||
      preventConsecutiveDayBooking !== club.preventConsecutiveDayBooking ||
      (lateExemptionNum ?? null) !== (club.lateBookingExemptionHours ?? null) ||
      (primeWeeklyNum ?? null) !== (club.maxPrimeBookingsPerWeek ?? null) ||
      requirePlayerRoster !== club.requirePlayerRoster;
    return { identity, features, limits };
  }, [
    club,
    name2,
    slug2,
    eff,
    limitBookingsPerContactPerDay,
    maxContactNum,
    creationDayNum,
    enforceBookingQuotas,
    memberDailyNum,
    preventConsecutiveDayBooking,
    lateExemptionNum,
    primeWeeklyNum,
    requirePlayerRoster,
  ]);
  const isDirty = dirty.identity || dirty.features || dirty.limits;

  // ── Mutation: fire only the PUTs whose tab is dirty ─────────────────────
  const mutation = useMutation({
    mutationFn: async () => {
      if (!club) throw new Error("no club");
      const calls: Promise<unknown>[] = [];
      if (dirty.identity) {
        const parsed = clubIdentityUpdateSchema.safeParse({
          name: name2,
          slug: slug2,
        });
        if (!parsed.success) {
          throw {
            error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
          };
        }
        calls.push(apiPut(`/api/admin/clubs/${club.id}/identity`, parsed.data));
      }
      if (dirty.features) {
        const parsed = clubFeaturesSchema.safeParse({
          collectPhone: eff.collectPhone,
          requirePhone: eff.requirePhone,
          requirePhoneOtp: eff.requirePhoneOtp,
          sendBookingConfirmationSms: eff.sendBookingConfirmationSms,
          collectEmail: eff.collectEmail,
          requireEmail: eff.requireEmail,
          requireEmailOtp: eff.requireEmailOtp,
          sendBookingConfirmationEmail: eff.sendBookingConfirmationEmail,
          requireLogin: eff.requireLogin,
          requireMembership: eff.requireMembership,
          allowMemberOnlySlots: eff.allowMemberOnlySlots,
          allowPrimeTimeSlots: eff.allowPrimeTimeSlots,
          showPublicSchedule: eff.showPublicSchedule,
          // Not editable from this SUPERADMIN dialog — pull the current values
          // off the loaded club so the schema check passes and a contact save
          // doesn't wipe reminders/comment configured on the Features page.
          sendBookingReminderSms:
            (club as { sendBookingReminderSms?: boolean })
              .sendBookingReminderSms ?? false,
          sendBookingReminderEmail:
            (club as { sendBookingReminderEmail?: boolean })
              .sendBookingReminderEmail ?? false,
          collectBookingComment:
            (club as { collectBookingComment?: boolean })
              .collectBookingComment ?? false,
        });
        if (!parsed.success) {
          throw {
            error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
          };
        }
        calls.push(apiPut(`/api/admin/clubs/${club.id}/features`, parsed.data));
      }
      if (dirty.limits) {
        const parsed = clubLimitsSchema.safeParse({
          limitBookingsPerContactPerDay,
          maxBookingsPerContactPerDay: Number.isFinite(maxContactNum)
            ? maxContactNum
            : club.maxBookingsPerContactPerDay,
          maxBookingsPerCreationDay: creationDayNum,
          enforceBookingQuotas,
          maxBookingsPerMemberPerDay: Number.isFinite(memberDailyNum)
            ? memberDailyNum
            : club.maxBookingsPerMemberPerDay,
          preventConsecutiveDayBooking,
          lateBookingExemptionHours: lateExemptionNum,
          maxPrimeBookingsPerWeek: primeWeeklyNum,
          requirePlayerRoster,
        });
        if (!parsed.success) {
          throw {
            error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
          };
        }
        calls.push(apiPut(`/api/admin/clubs/${club.id}/limits`, parsed.data));
      }
      await Promise.all(calls);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("clubs.settings.savedToast"));
      onOpenChange(false);
      await onSaved();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  const channelOptionLabels = {
    off: t("access.contact.off"),
    optional: t("access.contact.optional"),
    required: t("access.contact.required"),
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="md:max-w-2xl md:max-h-[90vh] md:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("clubs.settings.title")}</DialogTitle>
          <DialogDescription>
            {club ? t("clubs.settings.description", { name: club.name }) : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TabKey)}
          >
            <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 h-auto auto-rows-[2.25rem]">
              <TabsTrigger value="general">
                {t("clubs.settings.tabs.general")}
              </TabsTrigger>
              <TabsTrigger value="account">
                {t("clubs.settings.tabs.account")}
              </TabsTrigger>
              <TabsTrigger value="contact">
                {t("clubs.settings.tabs.contact")}
              </TabsTrigger>
              <TabsTrigger value="limits">
                {t("clubs.settings.tabs.limits")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="club-name" className="text-sm text-neutral-700">
                  {t("clubs.editDialog.nameLabel")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="club-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="club-slug" className="text-sm text-neutral-700">
                  {t("clubs.editDialog.slugLabel")}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-neutral-400 shrink-0">
                    sportscanner.com/
                  </span>
                  <Input
                    id="club-slug"
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    disabled={mutation.isPending}
                    aria-invalid={slugInvalid}
                    className={`rounded-lg border-neutral-200 flex-1 min-w-[180px] ${
                      slugInvalid ? "border-red-400" : ""
                    }`}
                  />
                </div>
                {slugInvalid && (
                  <p className="text-xs text-red-500">
                    {t("errors.api.CLUBSLUG_INVALID")}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="account" className="space-y-4 mt-4">
              <Row
                id="ff-require-login"
                label={t("clubs.featuresDialog.requireLogin.label")}
                hint={t("clubs.featuresDialog.requireLogin.hint")}
                checked={eff.requireLogin}
                onCheckedChange={(v) => {
                  setRequireLogin(v);
                  if (!v) setRequireMembership(false);
                }}
                disabled={mutation.isPending || requireMembership}
              />
              {eff.requireLogin && (
                <div className="pl-4 border-l-2 border-neutral-100 space-y-4">
                  <Row
                    id="ff-require-membership"
                    label={t("clubs.featuresDialog.requireMembership.label")}
                    hint={t("clubs.featuresDialog.requireMembership.hint")}
                    checked={requireMembership}
                    onCheckedChange={setRequireMembership}
                    disabled={mutation.isPending}
                  />
                  <Row
                    id="ff-send-email-account"
                    label={t(
                      "clubs.featuresDialog.sendBookingConfirmationEmail.label",
                    )}
                    hint={t(
                      "clubs.featuresDialog.sendBookingConfirmationEmail.hint",
                    )}
                    checked={sendBookingConfirmationEmail}
                    onCheckedChange={setSendBookingConfirmationEmail}
                    disabled={mutation.isPending}
                  />
                </div>
              )}
              <Row
                id="ff-allow-member-only-slots"
                label={t("clubs.featuresDialog.allowMemberOnlySlots.label")}
                hint={t("clubs.featuresDialog.allowMemberOnlySlots.hint")}
                checked={allowMemberOnlySlots}
                onCheckedChange={setAllowMemberOnlySlots}
                disabled={mutation.isPending}
              />
              {allowMemberOnlySlots && requireMembership && (
                <p className="text-xs text-neutral-500 pl-4 border-l-2 border-neutral-100">
                  {t("clubs.featuresDialog.allowMemberOnlySlots.redundantNote")}
                </p>
              )}
              <Row
                id="ff-allow-prime-time-slots"
                label={t("clubs.featuresDialog.allowPrimeTimeSlots.label")}
                hint={t("clubs.featuresDialog.allowPrimeTimeSlots.hint")}
                checked={allowPrimeTimeSlots}
                onCheckedChange={setAllowPrimeTimeSlots}
                disabled={mutation.isPending}
              />
              <Row
                id="ff-show-public-schedule"
                label={t("clubs.featuresDialog.showPublicSchedule.label")}
                hint={t("clubs.featuresDialog.showPublicSchedule.hint")}
                checked={showPublicSchedule}
                onCheckedChange={setShowPublicSchedule}
                disabled={mutation.isPending}
              />
            </TabsContent>

            <TabsContent value="contact" className="space-y-6 mt-4">
              {eff.requireLogin && (
                <p className="text-xs text-neutral-500 leading-relaxed">
                  {t("clubs.featuresDialog.requireLoginContactNote")}
                </p>
              )}

              {/* Phone — hidden when login is required (the account is the
                  contact source). SMS confirmation/OTP stay unshipped. */}
              {!eff.requireLogin && (
                <section className="space-y-4">
                  <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                    {t("clubs.featuresDialog.phoneSection")}
                  </h3>
                  <ChannelRow
                    id="ff-phone-channel"
                    label={t("access.contact.phone")}
                    hint={t("access.contact.phoneHint")}
                    value={
                      eff.requirePhone
                        ? "required"
                        : eff.collectPhone
                          ? "optional"
                          : "off"
                    }
                    onValueChange={(v) => {
                      setRequirePhone(v === "required");
                      setCollectPhone(v === "optional" || v === "required");
                      if (v !== "required") setRequirePhoneOtp(false);
                    }}
                    optionLabels={channelOptionLabels}
                    disabled={mutation.isPending}
                  />
                </section>
              )}

              {/* Email — the channel select is hidden when login is required;
                  the confirmation toggle stays available (sent to the account
                  address). */}
              <section className="space-y-4">
                <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                  {t("clubs.featuresDialog.emailSection")}
                </h3>
                {!eff.requireLogin && (
                  <ChannelRow
                    id="ff-email-channel"
                    label={t("access.contact.email")}
                    hint={t("access.contact.emailHint")}
                    value={
                      eff.requireEmail
                        ? "required"
                        : eff.collectEmail
                          ? "optional"
                          : "off"
                    }
                    onValueChange={(v) => {
                      setRequireEmail(v === "required");
                      setCollectEmail(v === "optional" || v === "required");
                      if (v !== "required") setRequireEmailOtp(false);
                    }}
                    optionLabels={channelOptionLabels}
                    disabled={mutation.isPending}
                  />
                )}
                {(eff.collectEmail || eff.requireLogin) && (
                  <Row
                    id="ff-send-email"
                    label={t(
                      "clubs.featuresDialog.sendBookingConfirmationEmail.label",
                    )}
                    hint={t(
                      "clubs.featuresDialog.sendBookingConfirmationEmail.hint",
                    )}
                    checked={sendBookingConfirmationEmail}
                    onCheckedChange={setSendBookingConfirmationEmail}
                    disabled={mutation.isPending}
                  />
                )}
              </section>
            </TabsContent>

            <TabsContent value="limits" className="space-y-6 mt-4">
              {showLimitsIdentityHint && (
                <p className="text-xs text-neutral-500 leading-relaxed">
                  {t("clubs.settings.limits.identityHint")}
                </p>
              )}

              {showCreationDayLimit && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                    {t("clubs.settings.limits.perUserSection")}
                  </h3>
                  <Label
                    htmlFor="ff-max-creation-day"
                    className="text-sm text-neutral-700"
                  >
                    {t("clubs.settings.limits.maxPerCreationDay.label")}
                  </Label>
                  <p className="text-xs text-neutral-500">
                    {t("clubs.settings.limits.maxPerCreationDay.hint")}
                  </p>
                  <Input
                    id="ff-max-creation-day"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    placeholder={t(
                      "clubs.settings.limits.maxPerCreationDay.placeholder",
                    )}
                    value={maxBookingsPerCreationDay}
                    onChange={(e) =>
                      setMaxBookingsPerCreationDay(e.target.value)
                    }
                    disabled={mutation.isPending}
                    aria-invalid={creationDayInvalid}
                    className={`rounded-lg border-neutral-200 w-32 ${
                      creationDayInvalid ? "border-red-400" : ""
                    }`}
                  />
                  {creationDayInvalid && (
                    <p className="text-xs text-red-500">
                      {t("errors.api.BOOKING_LIMIT_INVALID")}
                    </p>
                  )}
                </section>
              )}

              {/* Contact-based cap — only shown when a phone or email channel
                  is collected and login/membership is off. With either on, the
                  booker already has an account identity and the per-member
                  daily cap covers the same case more precisely. */}
              {showContactLimit && (
                <section className="space-y-4">
                  <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                    {t("clubs.settings.limits.contactSection")}
                  </h3>
                  <Row
                    id="ff-limit-contact"
                    label={t("clubs.editDialog.limitBookings.label")}
                    hint={t("clubs.editDialog.limitBookings.hint")}
                    checked={limitBookingsPerContactPerDay}
                    onCheckedChange={setLimitBookingsPerContactPerDay}
                    disabled={mutation.isPending}
                  />
                  {limitBookingsPerContactPerDay && (
                    <div className="space-y-2 pl-4 border-l-2 border-neutral-100">
                      <Label
                        htmlFor="ff-max-contact"
                        className="text-sm text-neutral-700"
                      >
                        {t("clubs.editDialog.maxBookings.label")}
                      </Label>
                      <Input
                        id="ff-max-contact"
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={maxBookingsPerContactPerDay}
                        onChange={(e) =>
                          setMaxBookingsPerContactPerDay(e.target.value)
                        }
                        disabled={mutation.isPending}
                        aria-invalid={maxContactInvalid}
                        className={`rounded-lg border-neutral-200 w-32 ${
                          maxContactInvalid ? "border-red-400" : ""
                        }`}
                      />
                      {maxContactInvalid && (
                        <p className="text-xs text-red-500">
                          {t("errors.api.BOOKING_LIMIT_INVALID")}
                        </p>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* Member quotas — counted by player ACCOUNT identity (any
                  logged-in player), not membership. Shown only when accounts
                  can exist: login/membership is required, or member-only slots
                  are on (those bookings always carry an account). Without an
                  account, none of these rules can bind. */}
              {showMemberLimit && (
                <section className="space-y-4">
                  <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                    {t("clubs.settings.limits.memberSection")}
                  </h3>
                  <Row
                    id="ff-enforce-quotas"
                    label={t("clubs.settings.limits.enforce.label")}
                    hint={t("clubs.settings.limits.enforce.hint")}
                    checked={enforceBookingQuotas}
                    onCheckedChange={setEnforceBookingQuotas}
                    disabled={mutation.isPending}
                  />
                  {enforceBookingQuotas && (
                    <div className="pl-4 border-l-2 border-neutral-100 space-y-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="ff-max-member-day"
                          className="text-sm text-neutral-700"
                        >
                          {t("clubs.settings.limits.maxPerMemberPerDay.label")}
                        </Label>
                        <p className="text-xs text-neutral-500">
                          {t("clubs.settings.limits.maxPerMemberPerDay.hint")}
                        </p>
                        <Input
                          id="ff-max-member-day"
                          type="number"
                          min={1}
                          max={100}
                          step={1}
                          value={maxBookingsPerMemberPerDay}
                          onChange={(e) =>
                            setMaxBookingsPerMemberPerDay(e.target.value)
                          }
                          disabled={mutation.isPending}
                          className="rounded-lg border-neutral-200 w-32"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label
                          htmlFor="ff-late-exemption"
                          className="text-sm text-neutral-700"
                        >
                          {t("clubs.settings.limits.lateExemption.label")}
                        </Label>
                        <p className="text-xs text-neutral-500">
                          {t("clubs.settings.limits.lateExemption.hint")}
                        </p>
                        <Input
                          id="ff-late-exemption"
                          type="number"
                          min={1}
                          max={72}
                          step={1}
                          placeholder={t(
                            "clubs.settings.limits.lateExemption.placeholder",
                          )}
                          value={lateBookingExemptionHours}
                          onChange={(e) =>
                            setLateBookingExemptionHours(e.target.value)
                          }
                          disabled={mutation.isPending}
                          className="rounded-lg border-neutral-200 w-32"
                        />
                      </div>

                      <Row
                        id="ff-prevent-consecutive"
                        label={t(
                          "clubs.settings.limits.preventConsecutive.label",
                        )}
                        hint={t(
                          "clubs.settings.limits.preventConsecutive.hint",
                        )}
                        checked={preventConsecutiveDayBooking}
                        onCheckedChange={setPreventConsecutiveDayBooking}
                        disabled={mutation.isPending}
                      />

                      {allowPrimeTimeSlots && (
                        <div className="space-y-2">
                          <Label
                            htmlFor="ff-prime-week"
                            className="text-sm text-neutral-700"
                          >
                            {t("clubs.settings.limits.maxPrimePerWeek.label")}
                          </Label>
                          <p className="text-xs text-neutral-500">
                            {t("clubs.settings.limits.maxPrimePerWeek.hint")}
                          </p>
                          <Input
                            id="ff-prime-week"
                            type="number"
                            min={1}
                            max={100}
                            step={1}
                            placeholder={t(
                              "clubs.settings.limits.maxPrimePerWeek.placeholder",
                            )}
                            value={maxPrimeBookingsPerWeek}
                            onChange={(e) =>
                              setMaxPrimeBookingsPerWeek(e.target.value)
                            }
                            disabled={mutation.isPending}
                            className="rounded-lg border-neutral-200 w-32"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* Player roster — club-level toggle. Tennis/padel: singles or
                  doubles radio at booking time. Other sports: free-text list. */}
              <section className="space-y-3">
                <h3 className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                  {t("clubs.settings.limits.rosterSection")}
                </h3>
                <Row
                  id="ff-require-roster"
                  label={t("clubs.settings.limits.requireRoster.label")}
                  hint={t("clubs.settings.limits.requireRoster.hint")}
                  checked={requirePlayerRoster}
                  onCheckedChange={setRequirePlayerRoster}
                  disabled={mutation.isPending}
                />
              </section>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!isValid || !isDirty || mutation.isPending}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("common.save")
            )}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function Row({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="text-sm font-medium text-neutral-800">
          {label}
        </Label>
        <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

// A contact channel (phone / email) as an Off / Optional / Required dropdown.
function ChannelRow({
  id,
  label,
  hint,
  value,
  onValueChange,
  optionLabels,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onValueChange: (v: string) => void;
  optionLabels: { off: string; optional: string; required: string };
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="text-sm font-medium text-neutral-800">
          {label}
        </Label>
        <p className="text-xs text-neutral-500 mt-0.5">{hint}</p>
      </div>
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-36 rounded-lg border-neutral-200">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="off">{optionLabels.off}</SelectItem>
          <SelectItem value="optional">{optionLabels.optional}</SelectItem>
          <SelectItem value="required">{optionLabels.required}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
